// Determine BFF URL based on hostname
export function getBffUrl() {
  const hostname = window.location.hostname
  const pathname = window.location.pathname

  if (hostname === 'upcast.social') {
    return 'https://bff.upcast.social'
  }

  if (hostname === 'preview.upcast.social') {
    const match = pathname.match(/^\/([^/]+)\//)
    if (match) {
      return `https://${match[1]}.bff.upcast.social`
    }
  }

  return 'https://bff.upcast.social'
}

// URL for auth form actions (login/logout).
// Production uses same-origin paths (CloudFront routes /auth/* to API Gateway)
// to avoid Safari ITP blocking cross-origin Set-Cookie responses.
export function getAuthFormUrl() {
  const hostname = window.location.hostname
  const pathname = window.location.pathname

  if (hostname === 'upcast.social') {
    return ''
  }

  if (hostname === 'preview.upcast.social') {
    const match = pathname.match(/^\/([^/]+)\//)
    if (match) {
      return `https://${match[1]}.bff.upcast.social`
    }
  }

  return 'https://bff.upcast.social'
}

// Mock mode: auto-enabled in Vite dev unless explicitly disabled or running tests
export const MOCK_MODE = !import.meta.env?.VITEST && (
  import.meta.env?.VITE_MOCK_MODE === 'true'
  || (import.meta.env?.DEV && import.meta.env?.VITE_MOCK_MODE !== 'false')
)

// Mock data loaded from prod export (sensitive fields stripped).
// Files live in src/mock-data/ (gitignored). If missing, mock mode still works with empty data.
// To populate: run the DynamoDB export script (see CLAUDE.md dev commands).
let mockUsers = [], mockBrands = [], mockCampaigns = [], mockContent = []
let mockSchedules = [], mockConnections = [], mockInviteCodes = []
let mockIdeas = [], mockFeedback = [], mockSettings = []

if (MOCK_MODE) {
  try {
    const modules = import.meta.glob('../mock-data/*.json', { eager: true })
    const get = (name) => modules[`../mock-data/${name}.json`]?.default || []
    mockUsers = get('users')
    mockBrands = get('brands')
    mockCampaigns = get('campaigns')
    mockContent = get('content')
    mockSchedules = get('schedules')
    mockConnections = get('social-connections')
    mockInviteCodes = get('invite-codes')
    mockIdeas = get('ideas')
    mockFeedback = get('feedback')
    mockSettings = get('settings')
  } catch { /* no mock data files — use empty arrays */ }
}

// Mock login: any email matching a prod user logs in with password "password"
// Falls back to a built-in admin account if no mock data files exist.
const MOCK_FALLBACK_USERS = [
  { email: 'admin@upcast.social', role: 'admin', username: 'admin', first_name: 'Admin', last_name: 'User', user_id: 'user-admin-001' },
  { email: 'demo@example.com', role: 'user', username: 'demo', first_name: 'Demo', last_name: 'User', user_id: 'user-demo-001' },
]
const allMockUsers = mockUsers.length > 0 ? mockUsers : MOCK_FALLBACK_USERS
const MOCK_CREDENTIALS = Object.fromEntries(
  allMockUsers.map((u) => [u.email, u])
)

const mockDisplayName = (u) => u
  ? [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email
  : undefined
const mockEnrichContent = (c) => {
  const out = { ...c }
  if (c.user_id) {
    const creator = mockUsers.find((u) => u.user_id === c.user_id)
    if (creator) {
      out.created_by_email = creator.email
      out.created_by_name = mockDisplayName(creator)
    }
  }
  if (c.published_by) {
    const publisher = mockUsers.find((u) => u.user_id === c.published_by)
    if (publisher) {
      out.published_by_email = publisher.email
      out.published_by_name = mockDisplayName(publisher)
    }
  }
  if (c.campaign_id) {
    const camp = mockCampaigns.find((cc) => cc.campaign_id === c.campaign_id)
    if (camp) {
      out.campaign_name = camp.name || camp.title
      const campCreator = mockUsers.find((u) => u.user_id === camp.user_id)
      out.campaign_created_by_name = mockDisplayName(campCreator)
    }
  }
  return out
}

function getMockUser() {
  const stored = localStorage.getItem('smm_mock_user')
  return stored ? JSON.parse(stored) : null
}

export function mockLogin(email, password) {
  if (password !== 'password') return null
  const prodUser = MOCK_CREDENTIALS[email]
  if (!prodUser) return null
  const user = { ...prodUser, email }
  localStorage.setItem('smm_mock_user', JSON.stringify(user))
  return user
}

export function mockLogout() {
  localStorage.removeItem('smm_mock_user')
}

export function mockHasData() {
  return mockUsers.length > 0
}

const BFF_URL = getBffUrl()

// Mock API handler — serves real prod data filtered by current user
async function mockApiFetch(endpoint, options = {}) {
  await new Promise((r) => setTimeout(r, 100))
  const user = getMockUser()
  if (!user) {
    const err = new Error('Not authenticated')
    err.status = 401
    throw err
  }
  const method = options.method || 'GET'
  const isAdmin = user.role === 'admin'

  // Auth
  if (endpoint === '/auth/me') return user

  // Dashboard — aggregate endpoint
  if (endpoint === '/dashboard') {
    const brands = mockBrands.filter((b) => b.account_id === user.account_id)
    const brandIds = new Set(brands.map((b) => b.brand_id))
    const connections = mockConnections.filter((c) => c.user_id === user.user_id)
    const campaigns = mockCampaigns.filter((c) => brandIds.has(c.brand_id))
    const content = mockContent
      .filter((c) => brandIds.has(c.brand_id))
      .map(mockEnrichContent)
    const schedules = mockSchedules.filter((s) => brandIds.has(s.brand_id))
    return { user, brands, connections, campaigns, content, schedules }
  }

  // Social connections
  if (endpoint === '/social/connections') {
    const conns = mockConnections.filter((c) => c.user_id === user.user_id)
    return { connections: conns }
  }
  if (endpoint.startsWith('/social/')) return { success: true }

  // Brands — user sees all brands in their account
  if (endpoint === '/brands' && method === 'GET') {
    const brands = mockBrands.filter((b) => b.account_id === user.account_id)
    return { brands }
  }
  if (endpoint === '/brands' && method === 'POST') {
    return { brand_id: 'mock-brand-new', ...JSON.parse(options.body || '{}') }
  }
  const brandMatch = endpoint.match(/^\/brands\/([^/]+)$/)
  if (brandMatch && method === 'GET') {
    const brand = mockBrands.find((b) => b.brand_id === brandMatch[1])
    return brand || { error: 'Not found' }
  }
  const brandAssetsMatch = endpoint.match(/^\/brands\/([^/]+)\/assets/)
  if (brandAssetsMatch) return { assets: [] }

  // Onboarding (mock so dev preview shows the welcome card).
  // State is kept in sessionStorage so that mark toggles survive reloads
  // within the dev session but reset when you close the tab.
  if (endpoint === '/onboarding/status') {
    const mockState = (() => {
      try { return JSON.parse(sessionStorage.getItem('mock_onboarding_state') || '{}') }
      catch { return {} }
    })()
    const steps = [
      { id: 'connect_social', title: 'Connect a social account', done: true, cta_url: '/connections', guide_url: '/guides#connections' },
      { id: 'add_brand_asset', title: 'Add brand assets', done: !!mockState.asset_uploaded, cta_url: '/brands/mock/assets', guide_url: '/guides#brand-assets-section' },
      { id: 'create_post', title: 'Create a post', done: false, cta_url: '/content/create', guide_url: '/guides#creating-posts' },
      { id: 'schedule_post', title: 'Schedule a post', done: false, cta_url: '/content/create', guide_url: '/guides#scheduling' },
      { id: 'review_auto_post', title: 'Publish an auto-post', done: false, cta_url: '/content', guide_url: '/guides#reviewing-posts' },
      { id: 'first_campaign', title: 'Create your first campaign', done: false, cta_url: '/campaigns', guide_url: '/guides#campaigns' },
      { id: 'view_guide', title: 'Tour the dashboard', done: !!mockState.guide_viewed, cta_url: '/guides#dashboard-tour' },
    ]
    return {
      steps,
      complete: steps.every((s) => s.done),
      dismissed: !!mockState.dismissed,
    }
  }
  if (endpoint === '/onboarding/mark') {
    try {
      const { step, value } = JSON.parse(options.body || '{}')
      const state = JSON.parse(sessionStorage.getItem('mock_onboarding_state') || '{}')
      state[step] = value
      sessionStorage.setItem('mock_onboarding_state', JSON.stringify(state))
    } catch { /* ignore */ }
    return { ok: true }
  }

  // Content — filter by brand_id query param or user_id
  if (endpoint.startsWith('/content')) {
    if (method !== 'GET') return { success: true }
    const params = new URLSearchParams(endpoint.split('?')[1] || '')
    const brandId = params.get('brand_id')
    const contentIdMatch = endpoint.match(/^\/content\/([^/?]+)/)
    if (contentIdMatch && contentIdMatch[1] !== 'create') {
      const item = mockContent.find((c) => c.content_id === contentIdMatch[1])
      return item ? mockEnrichContent(item) : { error: 'Not found' }
    }
    const status = params.get('status')
    let items = mockContent
    if (brandId) items = items.filter((c) => c.brand_id === brandId)
    if (status) items = items.filter((c) => (c.status || 'draft') === status)
    return { content: items.map(mockEnrichContent) }
  }

  // Campaigns
  if (endpoint.startsWith('/campaigns')) {
    if (method !== 'GET') return { success: true }
    const params = new URLSearchParams(endpoint.split('?')[1] || '')
    const brandId = params.get('brand_id')
    const campMatch = endpoint.match(/^\/campaigns\/([^/?]+)/)
    if (campMatch && campMatch[1] !== 'new') {
      const camp = mockCampaigns.find((c) => c.campaign_id === campMatch[1])
      if (!camp) return { error: 'Not found' }
      const posts = mockContent
        .filter((c) => c.campaign_id === camp.campaign_id)
        .map(mockEnrichContent)
      return { campaign: camp, posts }
    }
    let items = mockCampaigns
    if (brandId) items = items.filter((c) => c.brand_id === brandId)
    return { campaigns: items }
  }

  // Schedules — scope to brands on the current user's account (matches
  // schedule-list prod lambda which filters via getAccessibleBrandIDs).
  if (endpoint.startsWith('/schedules')) {
    if (method !== 'GET') return { success: true }
    const params = new URLSearchParams(endpoint.split('?')[1] || '')
    const brandId = params.get('brand_id')
    const accountBrandIds = new Set(
      mockBrands.filter((b) => b.account_id === user.account_id).map((b) => b.brand_id)
    )
    let items = mockSchedules.filter((s) => accountBrandIds.has(s.brand_id))
    if (brandId) items = items.filter((s) => s.brand_id === brandId)
    const enriched = items.map((s) => {
      const out = { ...s }
      const content = mockContent.find((c) => c.content_id === s.content_id)
      if (content) {
        out.content_type = content.content_type || content.type || 'post'
        out.campaign_id = content.campaign_id
        if (content.campaign_id) {
          const camp = mockCampaigns.find((cc) => cc.campaign_id === content.campaign_id)
          if (camp) {
            out.campaign_name = camp.name || camp.title
            const campCreator = mockUsers.find((u) => u.user_id === camp.user_id)
            out.campaign_created_by_name = mockDisplayName(campCreator)
          }
        }
      }
      const brand = mockBrands.find((b) => b.brand_id === s.brand_id)
      if (brand) out.brand_name = brand.name
      const scheduler = mockUsers.find((u) => u.user_id === s.user_id)
      if (scheduler) out.username = mockDisplayName(scheduler) || scheduler.email
      return out
    })
    return { schedules: enriched }
  }

  // Admin endpoints
  if (endpoint === '/admin/invites' && method === 'GET') return { invites: mockInviteCodes }
  if (endpoint === '/admin/users' && method === 'GET') return { users: mockUsers }
  if (endpoint.startsWith('/admin/feedback')) return method === 'GET' ? { feedback: mockFeedback } : { success: true }
  if (endpoint.startsWith('/admin/settings')) return method === 'GET' ? { settings: mockSettings } : { success: true }
  if (endpoint.startsWith('/admin/')) return { success: true }

  // Analytics
  if (endpoint.startsWith('/analytics')) return { data: [] }

  // Ideas
  if (endpoint.startsWith('/ideas')) {
    if (method !== 'GET') return { success: true }
    return { ideas: mockIdeas }
  }

  // Engagement
  if (endpoint.startsWith('/engagement')) return { comments: [] }

  // Default
  if (method === 'GET') return {}
  return { success: true }
}

// Simple GET request cache with 30s TTL and in-flight deduplication
const _cache = new Map()
const _inflight = new Map()
const CACHE_TTL = 30_000
let _cacheGen = 0

function getCached(key) {
  const entry = _cache.get(key)
  if (entry && Date.now() - entry.ts < CACHE_TTL) return entry.data
  _cache.delete(key)
  return undefined
}

function setCache(key, data) {
  _cache.set(key, { data, ts: Date.now() })
}

export function invalidateCache(prefix = '') {
  _cacheGen++
  if (!prefix) {
    _cache.clear()
    _inflight.clear()
    return
  }
  for (const key of _cache.keys()) {
    if (key.startsWith(prefix)) _cache.delete(key)
  }
  for (const key of _inflight.keys()) {
    if (key.startsWith(prefix)) _inflight.delete(key)
  }
}

// Generic fetch wrapper with credentials
async function apiFetch(endpoint, options = {}) {
  if (MOCK_MODE) {
    return mockApiFetch(endpoint, options)
  }
  const isGet = !options.method || options.method === 'GET'
  const cacheKey = `${BFF_URL}${endpoint}`

  // Cache/dedup only for GET requests
  if (isGet) {
    const cached = getCached(cacheKey)
    if (cached !== undefined) return cached
    const inflight = _inflight.get(cacheKey)
    if (inflight) return inflight
  }

  const gen = _cacheGen
  const promise = (async () => {
    const response = await fetch(`${BFF_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })

    if (!response.ok) {
      let errorMessage = `Request failed (${response.status})`
      let errorData = {}
      try {
        const text = await response.text()
        try {
          errorData = JSON.parse(text)
          errorMessage = errorData.error || errorData.message || errorData.detail || errorMessage
        } catch {
          // Non-JSON response — include truncated body for debugging
          if (text.length > 0) {
            errorMessage = `Request failed (${response.status}): ${text.substring(0, 200)}`
          }
        }
      } catch {
        // Could not read response body
      }
      const err = new Error(errorMessage)
      err.code = errorData.code
      err.status = response.status
      err.data = errorData
      throw err
    }

    return response.json()
  })()

  if (isGet) {
    _inflight.set(cacheKey, promise)
    try {
      const result = await promise
      if (gen === _cacheGen) setCache(cacheKey, result)
      return result
    } finally {
      _inflight.delete(cacheKey)
    }
  }

  return promise
}

// Auth
export const authApi = {
  me: () => apiFetch('/auth/me'),
  updateProfile: (data) => apiFetch('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
}

// Dashboard — single endpoint for all dashboard data
export const dashboardApi = {
  load: () => apiFetch('/dashboard'),
}

// Admin - Invites
export const invitesApi = {
  list: () => apiFetch('/admin/invites'),
  generate: (data) => apiFetch('/admin/invites', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  delete: (code) => apiFetch(`/admin/invites/${code}`, {
    method: 'DELETE',
  }),
  sendEmail: (code) => apiFetch(`/admin/invites/${code}/send`, {
    method: 'POST',
  }),
}

// Admin - Beta Signups
export const betaSignupsApi = {
  approve: (code) => apiFetch(`/admin/beta/${code}/approve`, {
    method: 'POST',
  }),
}

// Admin - Users
export const usersApi = {
  list: () => apiFetch('/admin/users'),
  disable: (username) => apiFetch(`/admin/users/${username}/disable`, {
    method: 'PUT',
  }),
  activate: (username) => apiFetch(`/admin/users/${username}/activate`, {
    method: 'POST',
  }),
  updateUser: (username, updates) => apiFetch(`/admin/users/${username}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
}

// Admin - Feedback
export const feedbackApi = {
  submit: (data) => apiFetch('/feedback', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  list: (status = '') => apiFetch(`/admin/feedback${status ? `?status=${status}` : ''}`),
  update: (id, data) => apiFetch(`/admin/feedback/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
}

// Support tickets. End users use listMine/get/reply. Handlers (reseller or
// upcast admin) use listHandler/get/reply/update/escalate. Backend enforces
// authz per ticket regardless of which client method was called.
export const supportApi = {
  create: (data) => apiFetch('/support/tickets', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  listMine: (status = '') => apiFetch(`/support/tickets?scope=mine${status ? `&status=${status}` : ''}`),
  listHandler: (status = '') => apiFetch(`/support/tickets?scope=handler${status ? `&status=${status}` : ''}`),
  get: (id) => apiFetch(`/support/tickets/${id}`),
  reply: (id, data) => apiFetch(`/support/tickets/${id}/reply`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiFetch(`/support/tickets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  escalate: (id, data) => apiFetch(`/support/tickets/${id}/escalate`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getConfig: () => apiFetch('/support/config'),
  putConfig: (data) => apiFetch('/support/config', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
}

// Admin - Settings / Feature Flags
export const settingsApi = {
  list: () => apiFetch('/admin/settings'),
  update: (key, data) => apiFetch(`/admin/settings/${key}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
}

// Brands
export const brandsApi = {
  list: () => apiFetch('/brands'),
  get: (id) => apiFetch(`/brands/${id}`),
  create: (data) => apiFetch('/brands', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiFetch(`/brands/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiFetch(`/brands/${id}`, {
    method: 'DELETE',
  }),
  scrape: (id) => apiFetch(`/brands/${id}/scrape`, {
    method: 'POST',
  }),
  listAssets: (id, { page = 1, perPage = 100 } = {}) =>
    apiFetch(`/brands/${id}/assets?page=${page}&per_page=${perPage}`),
  uploadAsset: (id, data) => apiFetch(`/brands/${id}/assets`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  deleteAsset: (brandId, assetId) => apiFetch(`/brands/${brandId}/assets/${assetId}`, {
    method: 'DELETE',
  }),
}

// Content
export const contentApi = {
  list: (brandId, status = '', { limit, nextToken, includeContributors } = {}) => {
    const params = new URLSearchParams({ brand_id: brandId })
    if (status) params.set('status', status)
    if (limit) params.set('limit', String(limit))
    if (nextToken) params.set('next_token', nextToken)
    if (includeContributors) params.set('include_contributors', 'true')
    return apiFetch(`/content?${params}`)
  },
  get: (id) => apiFetch(`/content/${id}`),
  create: (data) => apiFetch('/content', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiFetch(`/content/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiFetch(`/content/${id}`, {
    method: 'DELETE',
  }),
}

// AI Content Generation
export const aiApi = {
  generatePost: (data) => apiFetch('/ai/generate/post', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  rewrite: (data) => apiFetch('/ai/rewrite', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  generateVariants: (data) => apiFetch('/ai/variants', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  generateBlog: (data) => apiFetch('/ai/generate/blog', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  repurpose: (data) => apiFetch('/ai/repurpose', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  blogToPosts: (data) => apiFetch('/ai/blog-to-posts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  imageQuery: (data) => apiFetch('/ai/image-query', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
}

// Jobs (for async operations)
export const jobsApi = {
  getStatus: (jobId) => apiFetch(`/jobs/${jobId}`),
}

// Schedules
export const schedulesApi = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return apiFetch(`/schedules${query ? `?${query}` : ''}`)
  },
  create: (data) => apiFetch('/schedules', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  cancel: (id) => apiFetch(`/schedules/${id}`, {
    method: 'DELETE',
  }),
  retry: (id) => apiFetch(`/schedules/${id}/retry`, {
    method: 'POST',
  }),
}

// Social Connections
export const socialApi = {
  list: () => apiFetch('/social/connections'),
  listAll: () => apiFetch('/social/connections?include_all=true'),
  connect: (platform, data = {}) => apiFetch(`/social/connect/${platform}`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  connectBluesky: (data) => apiFetch('/social/connect/bluesky', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  disconnect: (id) => apiFetch(`/social/connections/${id}`, {
    method: 'DELETE',
  }),
  purge: (id) => apiFetch(`/social/connections/${id}/purge`, {
    method: 'DELETE',
  }),
  getConfigStatus: () => apiFetch('/social/config'),
  saveConfig: (platform, data) => apiFetch(`/social/config/${platform}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  checkHealth: () => apiFetch('/social/connections/health-check', { method: 'POST' }),
  linkedinOrgs: () => apiFetch('/social/linkedin/organizations'),
  // Connect LinkedIn org access (Community Management API - separate from main LinkedIn connection)
  connectLinkedinOrg: (brandId) => apiFetch('/social/connect/linkedin_org', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId }),
  }),
  // Connect LinkedIn repost org access (for auto-resharing from secondary org page)
  connectLinkedinRepostOrg: (brandId) => apiFetch('/social/connect/linkedin_org', {
    method: 'POST',
    body: JSON.stringify({ brand_id: brandId, is_repost_org: true }),
  }),
}

// Publishing
export const publishApi = {
  publish: (contentId, data = {}) => apiFetch(`/content/${contentId}/publish`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
}

// Analytics
export const analyticsApi = {
  dashboard: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return apiFetch(`/analytics/dashboard${query ? `?${query}` : ''}`)
  },
  get: (contentId) => apiFetch(`/analytics/${contentId}`),
  explain: (contentId) => apiFetch(`/ai/explain/${contentId}`, {
    method: 'POST',
  }),
  recommendations: (brandId) => apiFetch(`/ai/recommendations?brand_id=${brandId}`),
}

// Engagement
export const engagementApi = {
  comments: (contentId) => apiFetch(`/engagement/${contentId}/comments`),
  suggestReply: (data) => apiFetch('/ai/suggest-reply', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  reply: (data) => apiFetch('/engagement/reply', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
}

// Ideas
export const ideasApi = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return apiFetch(`/ideas${query ? `?${query}` : ''}`)
  },
  create: (data) => apiFetch('/ideas', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  convert: (id) => apiFetch(`/ideas/${id}/convert`, {
    method: 'POST',
  }),
  mineDocument: (data) => apiFetch('/ai/mine/document', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  mineUrl: (data) => apiFetch('/ai/mine/url', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
}

// Campaigns
export const campaignsApi = {
  list: (brandId) => apiFetch(`/campaigns${brandId ? `?brand_id=${brandId}` : ''}`),
  create: (data) => apiFetch('/campaigns', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  get: (id) => apiFetch(`/campaigns/${id}`),
  update: (id, data) => apiFetch(`/campaigns/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  plan: (data) => apiFetch(`/ai/plan-campaign`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  approve: (id) => apiFetch(`/campaigns/${id}/approve`, {
    method: 'POST',
  }),
  approvePost: (campaignId, postId) => apiFetch(`/campaigns/${campaignId}/posts/${postId}/approve`, {
    method: 'POST',
  }),
}

// Founder Mode
export const founderApi = {
  addSamples: (brandId, data) => apiFetch(`/brands/${brandId}/voice-samples`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  learnVoice: (brandId) => apiFetch(`/ai/learn-voice/${brandId}`, {
    method: 'POST',
  }),
  polish: (data) => apiFetch('/ai/founder-polish', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
}

// Image Search (Unsplash)
export const imageSearchApi = {
  search: (query) => apiFetch(`/images/search?q=${encodeURIComponent(query)}`),
  trackDownload: (data) => apiFetch('/images/track-download', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
}

// Preview Activity (scoped to current environment)
export const previewActivityApi = {
  getActivity: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return apiFetch(`/preview-activity${qs ? '?' + qs : ''}`)
  },
}

// Admin API Keys
export const apiKeysApi = {
  list: () => apiFetch('/admin/api-keys'),
  update: (key, data) => apiFetch(`/admin/api-keys/${key}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
}

// Admin - Auto Content
export const autoContentApi = {
  triggerRun: (brandIds) => apiFetch('/admin/auto-content/run', {
    method: 'POST',
    body: JSON.stringify(brandIds?.length ? { brand_ids: brandIds } : {}),
  }),
}

// Admin - Products
export const productsApi = {
  list: () => apiFetch('/admin/products'),
  upsert: (productId, data) => apiFetch(`/admin/products/${productId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (productId) => apiFetch(`/admin/products/${productId}`, {
    method: 'DELETE',
  }),
  listPublic: () => apiFetch('/products/public'),
}

// Admin - Accounts
export const accountsApi = {
  list: () => apiFetch('/admin/accounts'),
  get: (accountId) => apiFetch(`/admin/accounts/${accountId}`),
  upsert: (accountId, data) => apiFetch(`/admin/accounts/${accountId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  archive: (accountId, archived) => apiFetch(`/admin/accounts/${accountId}/archive`, {
    method: 'PUT',
    body: JSON.stringify({ archived }),
  }),
  assign: (type, id, accountId) => apiFetch('/admin/assign-account', {
    method: 'PUT',
    body: JSON.stringify({ type, id, account_id: accountId }),
  }),
  costSummary: () => apiFetch('/admin/cost-summary'),
}

// Reseller
export const resellerApi = {
  listClients: () => apiFetch('/reseller/clients'),
  createClient: (data) => apiFetch('/reseller/clients', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateClient: (accountId, data) => apiFetch(`/reseller/clients/${accountId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  inviteUser: (accountId, data) => apiFetch(`/reseller/clients/${accountId}/invite`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  removeUser: (accountId, username) => apiFetch(`/reseller/clients/${accountId}/users/${username}`, {
    method: 'DELETE',
  }),
  cancelInvite: (accountId, code) => apiFetch(`/reseller/clients/${accountId}/invites/${code}`, {
    method: 'DELETE',
  }),
}

// Co-branding
export const brandingApi = {
  me: () => apiFetch('/branding/me'),
  getReseller: () => apiFetch('/reseller/branding'),
  updateReseller: (data) => apiFetch('/reseller/branding', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  presignLogo: (contentType) => apiFetch('/reseller/branding/logo', {
    method: 'POST',
    body: JSON.stringify({ content_type: contentType }),
  }),
  uploadLogo: async (file) => {
    const presign = await brandingApi.presignLogo(file.type)
    const uploadRes = await fetch(presign.upload_url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    })
    if (!uploadRes.ok) throw new Error(`Upload failed (${uploadRes.status})`)
    return brandingApi.updateReseller({ logo_s3_key: presign.s3_key })
  },
}

// Onboarding
export const onboardingApi = {
  status: () => apiFetch('/onboarding/status'),
  mark: (step, value) => apiFetch('/onboarding/mark', {
    method: 'POST',
    body: JSON.stringify({ step, value }),
  }),
}

// Smart Scheduling
export const smartScheduleApi = {
  optimalTime: (brandId) => apiFetch(`/ai/optimal-time/${brandId}`),
  smartSchedule: (data) => apiFetch('/schedules/smart', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
}
